import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { rolesFromRestV1 } from 'domain/mappers/roleFromRestV1';
import type { RoleFE } from 'domain/systemAdmin';

import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { correlationHeaders, managementRestApi } from './restClient';

/**
 * `GET /api/v1/auth/roles` -> `RoleFE[]`. REST-only slice, so its base query is never invoked.
 */
const listRolesFn = async (): Promise<{ data: RoleFE[] }> => {
    try {
        const response = await managementRestApi.get<unknown>('/api/v1/auth/roles', correlationHeaders());
        return { data: rolesFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[listRoles]: Unexpected error occurred while getting the roles', loggableError);
    }
};

export const authApi = createApi({
    reducerPath: 'auth',
    // The endpoint runs on `queryFn`, so this is never invoked — and keeping a GraphQL base query
    // on a slice that issues no GraphQL request would be misleading.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        // The `string` arg is the cache key, not an input — `'ListRoles'`, unchanged from the
        // GraphQL endpoint. Do not "clean up" the unread argument.
        listRoles: builder.query<RoleFE[], string>({
            queryFn: listRolesFn,
        }),
    }),
});

export const { useListRolesQuery } = authApi;
